import React, { useEffect, useState } from "react";
import {
  Box,
  Flex,
  Heading,
  HStack,
  Badge,
  Button,
  ButtonGroup,
  Spinner,
  Text,
  Icon,
  IconButton,
  useToast,
} from "@chakra-ui/react";
import {
  FiImage,
  FiEye,
  FiCheckCircle,
  FiChevronLeft,
  FiChevronRight,
  FiEdit,
} from "react-icons/fi";
import { ImageCard } from "../cards";
import EditImageModal from "../EditImageModal";
import apiClient from "../../../Configuration/apiClient";
import API_CONFIG from "../../../Configuration/apiConfig";


export function VisualEvidence({
  isOrgUser,
  actualImageUrl,
  annotatedImageUrl,
  modalActualImages,
  modalAnnotatedImages,
  modalImageIndex,
  shelfInfo,
  cooler,
  onPrev,
  onNext,
  onRecalculate,
  isRecalculating,
  onValidateAvl,
  isValidatingAvl,
  // Edit modal
  isEditOpen,
  onEditOpen,
  onEditClose,
  onEditSave,
  runId,
  storeId,
  fileName,
  auditDate,
  validationInfo,
}) {
  const toast = useToast();

  // Holds only successfully-saved overrides, keyed by shelf number, so a
  // failed update never shows a value that wasn't actually persisted.
  const [purityOverrides, setPurityOverrides] = useState({});
  const [savingPurity, setSavingPurity] = useState(false);

  useEffect(() => {
    setPurityOverrides({});
  }, [cooler]);

  const currentShelfNumber = shelfInfo?.[modalImageIndex]?.shelfNumber ?? modalImageIndex + 1;
  const currentShelf = cooler?.shelfWiseCounts?.find(
    (s) => s.shelfNumber === currentShelfNumber
  );
  const currentPurity = (purityOverrides[currentShelfNumber] ?? currentShelf?.status)?.toUpperCase();

  const handleTogglePurity = async (newStatus) => {
    if (!currentShelf || currentPurity === newStatus || savingPurity) return;

    const userId = localStorage.getItem("userId") || sessionStorage.getItem("userId");
    if (!runId || !storeId || !fileName || !auditDate || !userId) {
      toast({
        title: "Missing data",
        description: "Required information is missing to update shelf purity.",
        status: "warning", duration: 3000, isClosable: true, position: "top",
      });
      return;
    }

    setSavingPurity(true);
    try {
      await apiClient.put(API_CONFIG.ENDPOINTS.STORE_REPORT.UPDATE_SHELF_PURITY, {
        runId: Number(runId),
        storeId,
        fileName,
        shelfPurity: newStatus,
        auditDate,
        userId: Number(userId),
      });
      setPurityOverrides((prev) => ({ ...prev, [currentShelfNumber]: newStatus }));
      toast({ title: "Shelf purity updated successfully", status: "success", duration: 2000, isClosable: true, position: "top" });
    } catch (err) {
      toast({
        title: "Failed to update shelf purity. Please try again.",
        description: err?.response?.data?.message,
        status: "error", duration: 3000, isClosable: true, position: "top",
      });
    } finally {
      setSavingPurity(false);
    }
  };

  return (
    <Box
      bg="white"
      borderRadius="xl"
      border="1px solid"
      borderColor="gray.200"
      boxShadow="lg"
      p={6}
    >
      <Flex align="center" justify="space-between" mb={4}>
        <Flex align="center">
          <Icon as={FiImage} color="red.600" boxSize={6} mr={3} />
          <Heading size="md" color="gray.800" fontWeight="semibold">
            Visual Evidence
          </Heading>
        </Flex>
        <HStack spacing={3}>
          {currentShelf && (
            <HStack spacing={2}>
              <ButtonGroup size="xs" isAttached variant="outline" isDisabled={savingPurity}>
                <Button
                  colorScheme="green"
                  variant={currentPurity === "PURE" ? "solid" : "outline"}
                  onClick={() => handleTogglePurity("PURE")}
                >
                  PURE
                </Button>
                <Button
                  colorScheme="red"
                  variant={currentPurity === "IMPURE" ? "solid" : "outline"}
                  onClick={() => handleTogglePurity("IMPURE")}
                >
                  IMPURE
                </Button>
              </ButtonGroup>
              {savingPurity && <Spinner size="xs" color="red.500" />}
            </HStack>
          )}
          {shelfInfo.length > 0 && shelfInfo[modalImageIndex] && (
            <Badge
              colorScheme="red"
              fontSize="md"
              px={4}
              py={2}
              borderRadius="full"
              fontWeight="bold"
            >
              {shelfInfo[modalImageIndex].label}
            </Badge>
          )}
        </HStack>
      </Flex>

      <HStack spacing={6} mb={4} minH="300px">
        <ImageCard
          title="Actual Image"
          subtitle="Original store photograph"
          icon={FiEye}
          imageUrl={actualImageUrl}
          colorScheme="red"
        />
        {!isOrgUser && (
          <ImageCard
            title="Annotated Image"
            subtitle="AI-processed analysis"
            icon={FiCheckCircle}
            imageUrl={annotatedImageUrl}
            colorScheme="green"
          />
        )}
      </HStack>

      <HStack justify="space-between" mt={4} spacing={4}>
        <Box width="200px" />

        {/* Image navigation */}
        <HStack spacing={4}>
          <IconButton
            aria-label="Previous image"
            icon={<FiChevronLeft />}
            onClick={onPrev}
            isDisabled={modalImageIndex <= 0}
            colorScheme="red"
            variant="outline"
            size="md"
          />
          <Text
            fontSize="md"
            color="gray.700"
            fontWeight="semibold"
            minW="80px"
            textAlign="center"
          >
            {modalActualImages.length > 0
              ? `${modalImageIndex + 1} / ${modalActualImages.length}`
              : "0 / 0"}
          </Text>
          <IconButton
            aria-label="Next image"
            icon={<FiChevronRight />}
            onClick={onNext}
            isDisabled={
              modalImageIndex >= Math.max(0, modalActualImages.length - 1)
            }
            colorScheme="red"
            variant="outline"
            size="md"
          />
        </HStack>

        {/* Action buttons */}
        <Flex align="center" justify="space-between" gap={6}>
          <Button
            leftIcon={<FiEdit />}
            colorScheme="red"
            variant="outline"
            size="sm"
            onClick={onEditOpen}
            _hover={{
              bg: "red.50",
              transform: "translateY(-2px)",
              boxShadow: "md",
            }}
            transition="all 0.2s"
          >
            Edit
          </Button>

          <EditImageModal
            isOpen={isEditOpen}
            onClose={onEditClose}
            runId={runId}
            storeId={storeId}
            fileName={fileName}
            preloadedImageUrl={actualImageUrl}
            onSave={onEditSave}
            auditDate={auditDate}
          />

          <Button
            colorScheme="red"
            variant="outline"
            size="sm"
            onClick={onRecalculate}
            isLoading={isRecalculating}
            loadingText="Recalculating.."
            _hover={{
              bg: "red.50",
              transform: "translateY(-2px)",
              boxShadow: "md",
            }}
            transition="all 0.2s"
          >
            ReCalculation
          </Button>

          <Button
            leftIcon={<FiCheckCircle />}
            colorScheme="green"
            variant="solid"
            size="sm"
            onClick={onValidateAvl}
            isLoading={isValidatingAvl}
            loadingText="Validating..."
            _hover={{
              bg: "green.600",
              transform: "translateY(-2px)",
              boxShadow: "md",
            }}
            transition="all 0.2s"
          >
            Validate
          </Button>
        </Flex>
      </HStack>
    </Box>
  );
}
